import { useTranslation } from 'react-i18next'

import { Card, Badge, Text, Grid, Button, Heading, Flex, Box } from '@percent/lemonade'
import { env } from '@percent/partner-dashboard/env'
import { formatMoney } from '@percent/utility'
import styles from './FundraiserDetailsCard.module.scss'
import { FundraiserProgress } from '@percent/domain/fundraiser'
import { getFormattedDate } from '@percent/partner-dashboard/common/library/utility/date'
import { FeaturedStarTooltip } from '@percent/partner-dashboard/common/components/FeaturedStarTooltip/FeaturedStarTooltip'
import { FundraiserDetailsCardProps } from './FundraiserDetailsCard.types'
import { FundraiserStatus } from '@percent/partner-dashboard/services/fundraisers/fundraisersService.types'

export function FundraiserDetailsCard({ fundraiserData }: FundraiserDetailsCardProps) {
  const {
    t,
    i18n: { language: locale }
  } = useTranslation()

  const fundraiserTitle = fundraiserData?.page?.title

  return (
    <Card className={styles.fundraiserDetailsCard}>
      <Grid gutter="l">
        <Grid.Col span={12}>
          <Flex direction="row" justify="space-between" align="center">
            <Flex gap={16} align="center">
              <Heading level="h3">{fundraiserTitle}</Heading>
              {fundraiserData.status === FundraiserStatus.Published ? (
                <Badge variant="positive">{t('status.open')}</Badge>
              ) : (
                <Badge variant="default">{t(`status.${fundraiserData.status}`)}</Badge>
              )}
              {fundraiserData.featured && (
                <Box className={styles.featuredStar}>
                  <FeaturedStarTooltip featured={fundraiserData.featured} />
                </Box>
              )}
            </Flex>
            <Flex>
              <Button
                variant="secondary"
                onClick={() =>
                  window.open(
                    `${env.NX_PUBLIC_WPG_ADDRESS}/fundraisers/${fundraiserData.id}`,
                    '_blank',
                    'noopener noreferrer'
                  )
                }
              >
                {t('button.view')}
              </Button>
            </Flex>
          </Flex>
        </Grid.Col>
      </Grid>
      <Grid gutter="xl">
        <Grid.Col span={12}>
          <Flex direction="row" gap={16}>
            <div className={`${styles.detailBox} ${styles.goalDetailBox}`}>
              <Text className={styles.label} size="small">
                {t('table.header.progress')}
              </Text>
              {fundraiserData.campaign.goal ? (
                <FundraiserProgress
                  donated={fundraiserData.donationTotal}
                  goal={fundraiserData.campaign.goal}
                  locale={locale}
                />
              ) : (
                <Text fontWeight="bold" dataTestId="fundraiser-raised">
                  {t('table.amount_raised', { amount: formatMoney(fundraiserData.donationTotal, { locale }) })}
                </Text>
              )}
            </div>
            <div className={styles.detailBox}>
              <Text className={styles.label} size="small">
                {t('table.header.nonprofit')}
              </Text>
              <Text fontWeight="bold" textWrap>
                {fundraiserData.organisation?.name}
              </Text>
            </div>
            <div className={styles.detailBox}>
              <Text className={styles.label} size="small">
                {t('table.header.organizer')}
              </Text>
              <Flex direction="column">
                <Text fontWeight="bold">{fundraiserData.organiser?.name}</Text>
                {fundraiserData.organiser?.email && (
                  <Text size="small" textWrap>
                    {fundraiserData.organiser.email}
                  </Text>
                )}
              </Flex>
            </div>
            <div className={styles.detailBox}>
              <Text className={styles.label} size="small">
                {t('table.header.createdAt')}
              </Text>
              <Text fontWeight="bold">{getFormattedDate(fundraiserData.createdAt)}</Text>
            </div>
          </Flex>
        </Grid.Col>
      </Grid>
    </Card>
  )
}
